import { Clause, Risk } from 'types/policy';

import {
  ANZSIC_TO_CPI_TYPES,
  CLAUSE_CODE_CPI_ELECTRICIAN_QLD,
  CLAUSE_CODE_CPI_PLUMBER_VICTORIA,
  CLAUSE_CODE_NAME_OF_LANDLORD,
  STAMP_DUTY_CLAUSE_CODE,
} from '../../constants/domain-specific';
import { parseLandlordName } from '../../hooks/parseLandlordName';
import { parsePlumbingLicences } from '../../hooks/parsePlumbingLicences';
import {
  updateCpiQLD,
  updateCpiVIC,
  updateLiabilityLimit,
  updatePlumbingLicences,
  updateStampdutyExempted,
  updateUseOfContractors,
} from '../../slices/coverDetails';
import { updateLandlordName } from '../../slices/extraInfo';
import { AppDispatch } from '../../store';
import { AnzsicCPIType } from '../../types/cover';
import { States } from '../../types/enums';
import { Occupation } from '../../types/refData';
import { RiskDetailBPKLiability } from '../../types/risk';
import { clauseExists } from './clauseExists';

const updateClauses = ({
  clauses,
  anzsicCode,
  isNSWAddress,
  dispatch,
}: {
  clauses: Clause[];
  anzsicCode: string | undefined;
  isNSWAddress: boolean;
  dispatch: AppDispatch;
}) => {
  if (clauseExists(clauses, CLAUSE_CODE_CPI_ELECTRICIAN_QLD)) {
    dispatch(updateCpiQLD(true));
  } else {
    anzsicCode &&
      ANZSIC_TO_CPI_TYPES[anzsicCode]?.includes(AnzsicCPIType.ElectricianQLD) &&
      dispatch(updateCpiQLD(false));
  }

  if (clauseExists(clauses, CLAUSE_CODE_CPI_PLUMBER_VICTORIA)) {
    dispatch(updateCpiVIC(true));
  } else {
    anzsicCode &&
      ANZSIC_TO_CPI_TYPES[anzsicCode]?.includes(AnzsicCPIType.PlumberVIC) &&
      dispatch(updateCpiVIC(false));
  }

  const stampDutyClauseExists = clauseExists(clauses, STAMP_DUTY_CLAUSE_CODE);

  stampDutyClauseExists && dispatch(updateStampdutyExempted(true));
  !stampDutyClauseExists && isNSWAddress && dispatch(updateStampdutyExempted(false));
  const cpiClause = clauses
    .flatMap((clause) => clause.clauseWording)
    .find((wording) => wording.clauseCode === CLAUSE_CODE_CPI_PLUMBER_VICTORIA);
  dispatch(updatePlumbingLicences(parsePlumbingLicences(cpiClause)));
  const landlordNameClause = clauses
    .flatMap((clause) => clause.clauseWording)
    .find((wording) => wording.clauseCode === CLAUSE_CODE_NAME_OF_LANDLORD);
  dispatch(updateLandlordName(parseLandlordName(landlordNameClause)));
};

export const updateLiabilityAction = ({
  liabilityRisk,
  occupation,
  dispatch,
}: {
  liabilityRisk: Risk;
  occupation: Occupation | undefined;
  dispatch: AppDispatch;
}) => {
  const { clauses } = liabilityRisk;
  const { stateCode } = liabilityRisk.location;
  const liabilityDetails = (liabilityRisk.riskDetail as RiskDetailBPKLiability).riskBPKLiability;
  const { limitOfLiability, paymentToContractors } = liabilityDetails;
  const anzsicCode = occupation?.anzsicCode;

  const isNSWAddress = stateCode === States.NSW;

  dispatch(updateLiabilityLimit(limitOfLiability!));
  dispatch(updateUseOfContractors(paymentToContractors!));

  if (clauses) {
    updateClauses({
      clauses,
      anzsicCode,
      isNSWAddress,
      dispatch,
    });
  } else {
    if (isNSWAddress) dispatch(updateStampdutyExempted(false));
    if (anzsicCode) {
      /**
       * @note if the ANZSIC code contains CPI, and a clause is not present, it is implied that the customer responded
       * with "No" to these questions, hence we can default these two to false in the coverDetails slice.
       */
      if (ANZSIC_TO_CPI_TYPES[anzsicCode]?.includes(AnzsicCPIType.ElectricianQLD)) {
        dispatch(updateCpiQLD(false));
      }
      if (ANZSIC_TO_CPI_TYPES[anzsicCode]?.includes(AnzsicCPIType.PlumberVIC)) {
        dispatch(updateCpiVIC(false));
      }
    }
  }
};
